import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import SearchSection from "@/components/SearchSection";
import ManualInputSection from "@/components/ManualInputSection";
import ResultsSection from "@/components/ResultsSection";
import Header from "@/components/Header";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [activeTab, setActiveTab] = useState("search"); // "search" or "manual"
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCalculate = async (nutrients) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/calculate`, nutrients);
      setAssessmentResult(response.data);
      toast.success("Assessment completed successfully!");
    } catch (error) {
      console.error("Error calculating score:", error);
      toast.error("Failed to calculate score. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    if (!assessmentResult) return;

    try {
      const response = await axios.post(
        `${API}/export-pdf`,
        assessmentResult,
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `nutrient-assessment-${assessmentResult.id}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Failed to export PDF. Please try again.");
    }
  };

  const handleNewAssessment = () => {
    setAssessmentResult(null);
    setActiveTab("search"); // Reset to search tab
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Header />

      <main className="container mx-auto px-4 py-12 max-w-7xl">
        {!assessmentResult ? (
          <>
            {/* Tab Selector */}
            <div className="flex justify-center mb-12">
              <div className="inline-flex rounded-xl bg-white shadow-md p-1.5" data-testid="tab-selector">
                <button
                  onClick={() => setActiveTab("search")}
                  className={`px-8 py-3 rounded-lg font-semibold transition-all duration-300 ${
                    activeTab === "search"
                      ? "bg-gradient-to-r from-[#64c2c8] to-[#cbcb3c] text-white shadow-lg"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                  data-testid="search-tab"
                >
                  Search Product
                </button>
                <button
                  onClick={() => setActiveTab("manual")}
                  className={`px-8 py-3 rounded-lg font-semibold transition-all duration-300 ${
                    activeTab === "manual"
                      ? "bg-gradient-to-r from-[#64c2c8] to-[#cbcb3c] text-white shadow-lg"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                  data-testid="manual-tab"
                >
                  Manual Entry
                </button>
              </div>
            </div>

            {/* Content Area */}
            <div className="max-w-4xl mx-auto">
              {activeTab === "search" ? (
                <SearchSection
                  onCalculate={handleCalculate}
                  loading={loading}
                />
              ) : (
                <ManualInputSection
                  onCalculate={handleCalculate}
                  loading={loading}
                />
              )}
            </div>
          </>
        ) : (
          <ResultsSection
            result={assessmentResult}
            onExportPDF={handleExportPDF}
            onNewAssessment={handleNewAssessment}
          />
        )}
      </main>
    </div>
  );
};

export default HomePage;
